import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Confetti,
  LockKey,
  SignIn,
  SquaresFour,
  UserPlus,
  Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import WebInviteRenderer from '../../features/webInvite/WebInviteRenderer';
import api, { getApiErrorMessage } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const ACTION_ROUTES = {
  rsvp: '/guest/rsvp',
  registry: '/guest/gifts',
  contacts: '/guest/contacts',
  actions: '/guest/overview',
  guest_home: '/guest/overview',
  itinerary: '/guest/itinerary',
  photos: '/guest/photos',
  travel: '/guest/tickets',
  accommodation: '/guest/room',
  wardrobe: '/guest/wardrobe',
};

const ACTION_LABELS = {
  rsvp: 'RSVP',
  registry: 'the gift registry',
  contacts: 'key contacts',
  actions: 'guest details',
  guest_home: 'guest details',
  itinerary: 'the itinerary',
  photos: 'event photos',
  travel: 'tickets and ID details',
  accommodation: 'room details',
  wardrobe: 'the wardrobe planner',
};

const GUEST_ACTION_ROLES = new Set(['guest', 'admin_guest', 'bride', 'groom', 'host']);

function toAbsoluteUrl(value) {
  if (!value) return null;
  try {
    return new URL(resolveMediaUrl(value), window.location.origin).toString();
  } catch (_error) {
    return null;
  }
}

function setMetadata(selector, attributes, restore) {
  let element = document.head.querySelector(selector);
  const created = !element;
  if (!element) {
    element = document.createElement(attributes.tagName || 'meta');
    document.head.appendChild(element);
  }
  const previous = {};
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === 'tagName') return;
    previous[key] = element.getAttribute(key);
    element.setAttribute(key, value);
  });
  restore.push(() => {
    if (created) {
      element.remove();
      return;
    }
    Object.entries(previous).forEach(([key, value]) => {
      if (value === null) element.removeAttribute(key);
      else element.setAttribute(key, value);
    });
  });
}

function useInviteMetadata(invite) {
  useEffect(() => {
    if (!invite?.slug) return undefined;
    const restore = [];
    const previousTitle = document.title;
    const seo = invite.design?.seo || {};
    const hero = invite.sections?.find((section) => section.type === 'hero');
    const venue = invite.sections?.find((section) => section.type === 'venue')?.content?.venues?.[0];
    const title = seo.title || `${invite.event?.name || 'A celebration'} · You are invited`;
    const description = seo.description || `Join us for ${invite.event?.name || 'a special celebration'}.`;
    const image = toAbsoluteUrl(
      seo.share_image_url
      || hero?.overrides?.image_url
      || invite.design?.layout?.background_image_url
      || hero?.content?.couple_photo_url
      || hero?.content?.hero_media?.find((item) => item.url)?.url,
    );
    const canonicalUrl = `${window.location.origin}/e/${invite.slug}`;

    document.title = title;
    setMetadata('meta[name="description"]', { name: 'description', content: description }, restore);
    setMetadata('meta[property="og:title"]', { property: 'og:title', content: title }, restore);
    setMetadata('meta[property="og:description"]', { property: 'og:description', content: description }, restore);
    setMetadata('meta[property="og:type"]', { property: 'og:type', content: 'website' }, restore);
    setMetadata('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl }, restore);
    setMetadata('meta[name="twitter:card"]', { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' }, restore);
    if (image) {
      setMetadata('meta[property="og:image"]', { property: 'og:image', content: image }, restore);
      setMetadata('meta[name="twitter:image"]', { name: 'twitter:image', content: image }, restore);
    }
    setMetadata('link[rel="canonical"]', { tagName: 'link', rel: 'canonical', href: canonicalUrl }, restore);

    const structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.dataset.webInviteMetadata = 'true';
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: invite.event?.name,
      startDate: invite.event?.start_date,
      endDate: invite.event?.end_date || invite.event?.start_date,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      url: canonicalUrl,
      ...(image ? { image: [image] } : {}),
      ...(venue ? {
        location: {
          '@type': 'Place',
          name: venue.name,
          address: venue.address,
        },
      } : {}),
    });
    document.head.appendChild(structuredData);

    return () => {
      document.title = previousTitle;
      structuredData.remove();
      restore.reverse().forEach((callback) => callback());
    };
  }, [invite]);
}

function LoadingInvite() {
  return (
    <main className="grid min-h-screen place-items-center bg-[#fffdf8] px-6 text-center">
      <div>
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-rose-50 text-rose-500">
          <Confetti className="h-7 w-7 animate-pulse" weight="duotone" />
        </div>
        <p className="mt-5 font-serif text-2xl text-slate-800">Opening your invitation…</p>
      </div>
    </main>
  );
}

function UnavailableInvite({ message }) {
  return (
    <main className="grid min-h-screen place-items-center bg-[#fffdf8] px-6 text-center">
      <div className="max-w-md">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-50 text-amber-700">
          <Warning className="h-7 w-7" weight="duotone" />
        </div>
        <h1 className="mt-5 font-serif text-3xl text-slate-900">This invitation is not available</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">{message || 'It may not have been published yet, or its address may have changed.'}</p>
        <Button asChild variant="outline" className="mt-7"><Link to="/"><ArrowLeft className="mr-2 h-4 w-4" />Go to GalaGo</Link></Button>
      </div>
    </main>
  );
}

export default function PublicWebInvitePage({ guestInvite = null }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    user,
    loading: authLoading,
    currentEvent,
    switchEvent,
  } = useAuth();
  const [invite, setInvite] = useState(guestInvite);
  const [loading, setLoading] = useState(!guestInvite);
  const [error, setError] = useState('');
  const [gate, setGate] = useState(null);
  const handledReturnActionRef = useRef('');

  useInviteMetadata(invite);

  useEffect(() => {
    if (guestInvite) {
      setInvite(guestInvite);
      setError('');
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setInvite(null);
    api.get(`/public/web-invites/${slug}`, { signal: controller.signal })
      .then((response) => setInvite(response.data))
      .catch((requestError) => {
        if (requestError.code === 'ERR_CANCELED') return;
        if (requestError.response?.status === 404) {
          setError('It may not have been published yet, or its address may have changed.');
        } else {
          setError(getApiErrorMessage(requestError, 'We could not open this invitation right now. Please try again shortly.'));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, guestInvite]);

  const config = useMemo(() => {
    if (!invite) return null;
    return { ...invite.design, sections: invite.sections || [] };
  }, [invite]);

  const clearReturnAction = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openGuestAction = useCallback(async (action) => {
    const destination = ACTION_ROUTES[action];
    if (!destination || !invite) return;

    if (!user) {
      setGate({ kind: 'auth', action });
      return;
    }

    setGate({ kind: 'checking', action });
    let event = currentEvent?.id === invite.event_id ? currentEvent : null;
    if (!event) event = await switchEvent(invite.event_id);

    const rawRole = event?.viewer_role || user.role;
    const eventRole = rawRole === 'manager' ? 'event_manager' : rawRole;
    if (!event || !GUEST_ACTION_ROLES.has(eventRole)) {
      setGate({ kind: 'denied', action });
      clearReturnAction();
      return;
    }

    setGate(null);
    navigate(destination);
  }, [clearReturnAction, currentEvent, invite, navigate, switchEvent, user]);

  useEffect(() => {
    const action = searchParams.get('action');
    if (!invite || authLoading || !ACTION_ROUTES[action]) return;
    const key = `${invite.event_id}:${action}:${user?.id || 'anonymous'}`;
    if (handledReturnActionRef.current === key) return;
    handledReturnActionRef.current = key;
    openGuestAction(action);
  }, [authLoading, invite, openGuestAction, searchParams, user?.id]);

  const authUrl = (path) => {
    const action = gate?.action;
    const returnTo = `${guestInvite ? '/guest' : `/e/${slug}`}${action ? `?action=${encodeURIComponent(action)}` : ''}`;
    const params = new URLSearchParams({ return_to: returnTo });
    if (invite?.event_id) params.set('event_id', invite.event_id);
    return `${path}?${params.toString()}`;
  };

  const goToAuth = (path) => navigate(authUrl(path));

  const submitWish = async (wish) => {
    const response = await api.post(`/public/web-invites/${slug}/wishes`, wish);
    setInvite((previous) => previous && ({
      ...previous,
      sections: previous.sections.map((section) => section.type === 'wishes'
        ? { ...section, content: { ...section.content, items: [response.data, ...(section.content?.items || [])] } }
        : section),
    }));
    toast.success('Your wish has been added');
  };

  if (loading) return <LoadingInvite />;
  if (error || !invite || !config) return <UnavailableInvite message={error} />;

  const actionLabel = ACTION_LABELS[gate?.action] || 'this guest feature';
  const viewerRole = currentEvent?.viewer_role === 'manager'
    ? 'event_manager'
    : (currentEvent?.viewer_role || user?.role);
  const canOpenGuestMenu = Boolean(
    user
    && currentEvent?.id === invite.event_id
    && GUEST_ACTION_ROLES.has(viewerRole),
  );

  return (
    <main className="min-h-screen bg-gradient-to-br from-stone-200 via-stone-100 to-rose-100">
      <div className="fixed left-1/2 top-3 z-50 flex w-full max-w-[430px] -translate-x-1/2 justify-end gap-2 px-3">
        {canOpenGuestMenu && (
          <Button type="button" variant="secondary" onClick={() => navigate('/guest/overview')} className="rounded-full bg-white/90 shadow-lg backdrop-blur hover:bg-white">
            <SquaresFour className="mr-2 h-4 w-4" />Guest menu
          </Button>
        )}
      </div>

      <WebInviteRenderer
        config={config}
        eventData={invite.event || {}}
        onAction={openGuestAction}
        onSubmitWish={invite.slug ? submitWish : undefined}
        wishAuthorName={user?.name || null}
      />

      <Dialog open={Boolean(gate)} onOpenChange={(open) => { if (!open && gate?.kind !== 'checking') setGate(null); }}>
        <DialogContent className="max-w-md">
          {gate?.kind === 'auth' && (
            <>
              <DialogHeader>
                <div className="mx-auto mb-2 grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary sm:mx-0"><LockKey className="h-5 w-5" weight="duotone" /></div>
                <DialogTitle>Sign in to open {actionLabel}</DialogTitle>
                <DialogDescription>
                  The invitation stays public. We only ask you to sign in when you use guest features such as RSVP, rooms or the registry.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-lg border bg-muted/35 p-3 text-xs leading-5 text-muted-foreground">
                New guests may need the invite code shared by the hosts before they can access private event details.
              </div>
              <DialogFooter className="gap-2 sm:flex-col sm:space-x-0">
                <Button className="w-full" onClick={() => goToAuth('/login')}><SignIn className="mr-2 h-4 w-4" />Sign in<ArrowRight className="ml-auto h-4 w-4" /></Button>
                <Button className="w-full" variant="outline" onClick={() => goToAuth('/register')}><UserPlus className="mr-2 h-4 w-4" />Create an account</Button>
              </DialogFooter>
            </>
          )}

          {gate?.kind === 'checking' && (
            <div className="py-8 text-center">
              <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
              <DialogTitle className="mt-5">Opening {actionLabel}…</DialogTitle>
              <DialogDescription className="mt-2">Checking your guest access for this event.</DialogDescription>
            </div>
          )}

          {gate?.kind === 'denied' && (
            <>
              <DialogHeader>
                <div className="mx-auto mb-2 grid h-11 w-11 place-items-center rounded-full bg-amber-100 text-amber-700 sm:mx-0"><Warning className="h-5 w-5" weight="duotone" /></div>
                <DialogTitle>This account is not on the guest list</DialogTitle>
                <DialogDescription>
                  Ask the hosts for your invite code, then join the event with that code before opening {actionLabel}.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter><Button variant="outline" onClick={() => setGate(null)}>Back to invitation</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
